import { useState } from 'react'
import {
    Box,
    Grid,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { ShoppingCart } from '@mui/icons-material'

import { useGetEcommerceQuery, useGetProjectConfigQuery } from '@/apis/v2Api'
import { exactEventsForRange } from '@/apis/v2Mappers'
import { useReportArgs } from '@/hooks/useReportArgs'
import type { Period } from '@/utils/dates'

import {
    ExactChip,
    Kpi,
    ReportHeader,
    ReportStatus,
    SectionCard,
    fmtMoney,
    fmtNum,
    fmtPct,
} from './common'

const STEP_LABELS: Record<string, string> = {
    view_item: 'Viewed item',
    add_to_cart: 'Added to cart',
    begin_checkout: 'Began checkout',
    purchase: 'Purchased',
}

/** v2 `/reports/ecommerce` — funnel, revenue and items. */
export function EcommercePage() {
    const [period, setPeriod] = useState<Period>('30d')
    const args = useReportArgs(period)
    const { data, isLoading, error } = useGetEcommerceQuery({ ...args, limit: 100 })
    const { data: cfg } = useGetProjectConfigQuery(args.projectId)
    const currency = cfg?.config.currency ?? 'USD'
    const exact = exactEventsForRange(
        cfg?.config,
        ['view_item', 'add_to_cart', 'begin_checkout', 'purchase', 'refund', 'purchase_item'],
        args.from,
    )
    const empty = !!data && data.funnel.every(s => !s.events) && !data.revenue

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Conversions"
                subtitle="Ecommerce funnel, revenue and items. Badged numbers are exact counts, not samples."
                period={period}
                onPeriodChange={setPeriod}
            />
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={empty}
                emptyIcon={<ShoppingCart />}
                emptyTitle="No ecommerce events in this range"
                emptyDescription="Send view_item, add_to_cart, begin_checkout and purchase events (purchases with value and items) to fill this report."
            >
                {data && (
                    <>
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Revenue"
                                    value={fmtMoney(data.revenue, currency)}
                                    exact={exact.has('purchase')}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Purchases"
                                    value={fmtNum(data.purchases)}
                                    exact={exact.has('purchase')}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Avg. order value"
                                    value={fmtMoney(data.aov, currency)}
                                    exact={exact.has('purchase')}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Conversion rate"
                                    value={fmtPct(data.conversion_rate)}
                                    hint="Sessions with a purchase / all sessions"
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Refunds"
                                    value={fmtMoney(data.refunds, currency)}
                                    exact={exact.has('refund')}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Items sold"
                                    value={fmtNum(data.items_sold)}
                                    exact={exact.has('purchase_item')}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <Kpi
                                    title="Cart value added"
                                    value={fmtMoney(data.cart_value, currency)}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mb: 3 }}>
                            <SectionCard title="Purchase funnel (sessions)">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Step</TableCell>
                                            <TableCell align="right">Events</TableCell>
                                            <TableCell align="right">Sessions</TableCell>
                                            <TableCell align="right">From previous</TableCell>
                                            <TableCell sx={{ width: '30%' }}>From start</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.funnel.map(s => (
                                            <TableRow key={s.step}>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {STEP_LABELS[s.step] ?? s.step}
                                                        {exact.has(s.step) && <ExactChip />}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(s.events)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(s.sessions)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtPct(s.rate_from_previous)}
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(
                                                                100,
                                                                (s.rate_from_start ?? 0) * 100,
                                                            )}
                                                            sx={{
                                                                flex: 1,
                                                                height: 8,
                                                                borderRadius: 4,
                                                            }}
                                                        />
                                                        <Typography variant="caption">
                                                            {fmtPct(s.rate_from_start)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </SectionCard>
                        </Box>

                        <SectionCard title="Items">
                            {data.items.length === 0 ? (
                                <Typography color="text.secondary" variant="body2">
                                    No item-level events (item_id) in this range.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item</TableCell>
                                            <TableCell align="right">Views</TableCell>
                                            <TableCell align="right">Add to carts</TableCell>
                                            <TableCell align="right">Cart rate</TableCell>
                                            <TableCell align="right">Qty purchased</TableCell>
                                            <TableCell align="right">Revenue</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.items.map(it => (
                                            <TableRow key={it.item_id} hover>
                                                <TableCell>{it.item_id}</TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(it.views)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(it.add_to_carts)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtPct(it.cart_rate)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtNum(it.purchased_qty)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {fmtMoney(it.revenue, currency)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </SectionCard>
                    </>
                )}
            </ReportStatus>
        </Box>
    )
}
